import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService, LearningTrailsService, NotificationService } from '@app/core';
import { LearningTrailsCategoryService } from '@app/core/services/learning-trails-category.service';
import { ProductsService } from '@app/core/services/products.service';
import { notNull, selectedEntity, selectedEntityActive } from '@app/shared/utils';
import { SIDENAV_END, SIDENAV_START, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { LocalStorageService } from 'ngx-webstorage';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { COLLECTION_KEY } from '../../components/collection-studio/collection-studio.component';
import { SidenavLearningStudioCollectionComponent } from '../../components/sidenav-learning-studio-collection/sidenav-learning-studio-collection.component';
import { SidenavLearningStudioComponent } from '../../components/sidenav-learning-studio/sidenav-learning-studio.component';
import { Category } from '../../models';
import { StudioFilterService, TrailsService } from '../../services';
import { SidenavStudioService } from '../../services/sidenav-studio.service';

@Component({
  templateUrl: './studio.component.html',
  standalone: false,
})
export class StudioComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly store = inject<Store<AppState>>(Store);
  private readonly router = inject(Router);
  private readonly trailsService = inject(TrailsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _cdrf = inject(ChangeDetectorRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _alertService = inject(AlertService);
  private readonly _productsService = inject(ProductsService);
  private readonly _translateService = inject(TranslateService);
  private readonly _storageService = inject(LocalStorageService);
  private readonly _studioFilterService = inject(StudioFilterService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _sidenavStudioService = inject(SidenavStudioService);
  private readonly learningTrailsService = inject(LearningTrailsService);
  private readonly _learningTrailsCategoryService = inject(LearningTrailsCategoryService);
  private readonly sidenavEnd = inject<Observable<SidenavRef>>(SIDENAV_END);
  private readonly sidenavData = inject<Observable<SidenavRef>>(SIDENAV_START);
  public countAllCourses: number;
  public categories$ = new BehaviorSubject<Category[]>([]);
  public categoriesSelected$ = new BehaviorSubject<number[]>([]);
  public collectionsSelected$ = new BehaviorSubject<number[]>([]);
  public coursesSelected$ = new BehaviorSubject<number[]>([]);
  public loading$ = new Subject<boolean>();
  public products$ = new BehaviorSubject<any[]>([]);
  public categoryTitleForms: { id: number; form: FormGroup }[];

  private _user$: Observable<any>;

  public ngOnInit(): void {
    this._user$ = this.store.select(AppSelectors.ActiveUser).pipe(takeUntilDestroyed(this.destroyRef), notNull());

    this._studioFilterService.collection$
      .pipe(notNull())
      .subscribe((collection) => this._sidenavStudioService.insertCollection(collection));

    this._user$
      .pipe(
        switchMap((user) => this._sidenavStudioService.categories$.pipe(map((categories) => ({ categories, user })))),
        tap(({ categories, user }) => {
          this.categoryTitleForms = categories.map((c) => {
            const category = {
              id: c.id,
              form: this._formBuilder.group({
                id: [c.id],
                nome: [c.nome, [Validators.required, Validators.minLength(3)]],
                entidades: c.entidades,
              }),
            };
            category.form.valueChanges
              .pipe(
                filter(() => category.form.valid),
                debounceTime(500),
              )
              .subscribe(({ id, nome }) => {
                const category = {
                  id,
                  nome,
                  entidades: selectedEntityActive(user),
                };
                if (!!id) {
                  this.editCategory(category);
                } else {
                  this.storeCategory(category);
                }
              });
            return category;
          });
        }),
      )
      .subscribe(({ categories }) => {
        this.categories$.next(categories);
        this.selectData(categories);
      });

    this._sidenavStudioService.categoriesSelected$.subscribe((categoriesSelected) =>
      this.categoriesSelected$.next(categoriesSelected),
    );

    this._sidenavStudioService.collectionsSelected$.subscribe((collectionsSelected) =>
      this.collectionsSelected$.next(collectionsSelected),
    );

    this._sidenavStudioService.coursesSelected$.subscribe((coursesSelected) =>
      this.coursesSelected$.next(coursesSelected),
    );

    this.sendCategory();
    this.getData();
  }

  public ngAfterViewInit(): void {
    this.sidenav
      .pipe(switchMap((sidenav) => sidenav.categoriesSelected$ as Observable<number[]>))
      .subscribe((categoriesSelected) => this._sidenavStudioService.passCategory(categoriesSelected));
    this.sidenav
      .pipe(switchMap((sidenav) => sidenav.collectionsSelected$ as Observable<number[]>))
      .subscribe((collectionsSelected) => this._sidenavStudioService.passCollection(collectionsSelected));
    this.sidenav
      .pipe(switchMap((sidenav) => sidenav.coursesSelected$ as Observable<number[]>))
      .subscribe((coursesSelected) => this._sidenavStudioService.passCourse(coursesSelected));
  }

  public ngOnDestroy(): void {
    this.categories$.complete();
    this.categoriesSelected$.complete();
    this.collectionsSelected$.complete();
    this.coursesSelected$.complete();
    this.loading$.complete();
  }

  public filtered(id: number, selecteds: number[]): boolean {
    return selecteds.includes(id);
  }

  public onCreateCategory() {
    this._user$.subscribe((user) => {
      const category = {
        id: null,
        nome: '',
        colecoes: [],
        entidades: selectedEntityActive(user),
        numberCourses: 0,
      };
      this._sidenavStudioService.editOrSaveCategory(category);
      setTimeout(() => {
        document.getElementById('new-category-input').focus();
      }, 0);
    });
  }

  public onOpenReportTemp(): void {
    this.router.navigate([`/${this.getUrlUcIvca()}/relatorio`, '']);
  }

  public onDeleteCategory(category: any) {
    if (!category.id) {
      this._sidenavStudioService.deleteCategory();
      this._notificationService.success(this._translateService.instant(`trilhas.estudio.categoria-excluida`));
      return;
    }
    this._alertService
      .confirm({
        message: this._translateService.instant(`trilhas.estudio.excluir-categoria`),
      })
      .pipe(
        notNull(),
        switchMap(() => this._learningTrailsCategoryService.deleteCategory(category.id)),
        catchError(() => of({ ret: 0 })),
      )
      .subscribe((res) => {
        if (res.ret === 1) {
          this._sidenavStudioService.deleteCategory(category.id);
          this._notificationService.success(this._translateService.instant(`trilhas.estudio.categoria-excluida`));
        } else {
          this._notificationService.error(this._translateService.instant(`trilhas.estudio.erro-excluir-categoria`));
        }
      });
  }

  public getCategoryTitleForm(category: any): FormGroup {
    return this.categoryTitleForms.find((c) => c.id === category.id).form;
  }

  public onSidenavCollection(category_id: number, collection?: any) {
    this.sidenavEnd.pipe(take(1)).subscribe((ref) => {
      ref.open({
        componentRef: SidenavLearningStudioCollectionComponent,
        closedAnchor: true,
        opened: true,
        data: { category_id, collection },
      });
      ref.closed$.pipe(take(1)).subscribe(() => ({}));
    });
  }

  public onCreateCollection(category_id: number, ordem: number) {
    this._user$.subscribe((user) => {
      this._sidenavStudioService.insertCollection({
        id: null,
        categoria_id: category_id,
        cursos: [],
        entidades: selectedEntityActive(user),
        nome: null,
        slug: null,
        autor: selectedEntityActive(user)[0].nome,
        destaque: false,
        cpb: selectedEntityActive(user)[0].tipo === 4 && selectedEntityActive(user)[0].id === 1,
        ordem,
      });
    });

    this._storageService.clear(COLLECTION_KEY);
    this._cdrf.markForCheck();
  }

  public onDropSection(event: CdkDragDrop<any>, catIndex: number) {
    if (event.currentIndex !== event.previousIndex) {
      const categories = this.categories$.value;
      const collections = categories[catIndex].colecoes;
      moveItemInArray(collections, event.previousIndex, event.currentIndex);
      categories[catIndex].colecoes = collections;
      this.categories$.next(categories);
      this.learningTrailsService
        .updateOrdem(
          categories[catIndex].colecoes[event.currentIndex].id,
          event.currentIndex + 1,
          event.previousIndex + 1,
        )
        .subscribe({
          next: (ret) => {
            if (ret.ret !== 1) {
              moveItemInArray(collections, event.currentIndex, event.previousIndex);
              this._notificationService.error(this._translateService.instant(`trilhas.estudio.erro-excluir-categoria`));
            }
          },
          error: () => {
            moveItemInArray(collections, event.currentIndex, event.previousIndex);
            this._notificationService.error(this._translateService.instant(`trilhas.estudio.erro-excluir-categoria`));
          },
        });
    }
  }

  private getUrlUcIvca(): string {
    return this.trailsService.uc$.value
      ? 'universidade-corporativa'
      : this.trailsService.ivca$.value
        ? 'ivca'
        : 'trilhas-de-aprendizagem';
  }

  private getData(): void {
    this.store
      .select(AppSelectors.ActiveUser)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        notNull(),
        tap(() => this.loading$.next(false)),
        tap((user) => {
          this._productsService.products(user.editora_id).subscribe((resp) => this.products$.next(resp.data as any[]));
        }),
        switchMap((user) => this.learningTrailsService.studio({ entidade: selectedEntityActive(user)[0] })),
        map(({ data }) => data),
        tap(() => this.loading$.next(true)),
        tap((data) => this.selectData(data)),
        map((data) =>
          data.map((category) => ({
            ...category,
            numberCourses: category.colecoes.reduce(
              (accumulator, collection) => accumulator + collection.cursos.length,
              0,
            ),
          })),
        ),
      )
      .subscribe((data) => {
        this._sidenavStudioService.passCategories(data);
      });
  }

  private selectData(data: any): void {
    const categories = [];
    const collections = [];
    const courses = [];
    data.forEach((category) => {
      categories.push(category.id);
      category.colecoes.forEach((collection) => {
        collections.push(collection.id);
        collection.cursos.forEach((course) => {
          courses.push(course.id);
        });
      });
    });

    this.categoriesSelected$.next(categories);
    this.collectionsSelected$.next(collections);
    this.coursesSelected$.next(courses);
  }

  private storeCategory(category: any) {
    this._learningTrailsCategoryService.postCategory(category).subscribe((resp) => {
      this._sidenavStudioService.editOrSaveCategory({ ...category, ...resp });
      this._notificationService.success(this._translateService.instant('geral.sucesso'));
      setTimeout(() => {
        document.getElementById(resp.id + '-category-input').scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    });
  }

  private editCategory(category: any) {
    if (category.entidades) {
      category.entidades = selectedEntity(category.entidades);
    }
    this._learningTrailsCategoryService.updateCategory(category).subscribe(() => {
      this._sidenavStudioService.editOrSaveCategory({ id: category.id, nome: category.nome });
      this._notificationService.success(this._translateService.instant('geral.sucesso'));
    });
  }

  private sendCategory() {
    this._sidenavStudioService.categories$
      .pipe(
        notNull(),
        takeUntilDestroyed(this.destroyRef),
        switchMap((data) => this.sidenav.pipe(tap((sidenav) => sidenav?.categories$?.next(data)))),
      )
      .subscribe(() => ({}));
  }

  private get sidenav() {
    return this.sidenavData?.pipe(
      notNull(),
      takeUntilDestroyed(this.destroyRef),
      exhaustMap((res) => res.componentInstance$),
      notNull(),
      distinctUntilChanged(isEqual),
      filter((res) => res instanceof SidenavLearningStudioComponent),
    );
  }
}
